/**
 * Address Lookup Field
 *
 * @example
 *  {{=tmpl['common.addressLookupSwiss']({
 *      name: 'addressLookupSwiss',
 *      validationType: 'addressLookupSwiss',
 *      className: 'my-address-lookup',
 *      label: 'playtech.mobile.registration.addressLookupSwiss.label',
 *      placeholder: 'playtech.mobile.registration.addressLookupSwiss.placeholder'
 *  })}}
 *
 * name {string}                - lookup field name
 * validationType {string}      - name of validation to pick from `user.validation.config.js`
 * className {string}           - custom class name
 * label {string}               - translation key
 * placeholder {string}         - translation key
 * active {boolean}             - disable or enable lookup field
 * innerFields {Array}          - fields config
 *
 * Clearly, you should provide some validation config for validation to work.
 */
define(function(require, exports, module) {
    'use strict';

    const _ = require('underscore');
    const config = require('config');
    const BaseView = require('common/base.view');
    const zipCityView = require('common/addressLookupSwiss/lookupControls/zipCityView');
    const streetNameView = require('common/addressLookupSwiss/lookupControls/streetNameView');
    const houseNumberView = require('common/addressLookupSwiss/lookupControls/houseNumberView');

    module.exports = BaseView.extend({
        options: {
            template: 'addressLookupSwissTmpl',
            label: '',
            placeholder: '',
            active: true,
            innerFields: []
        },

        events: {
            'tap .fn-action-manual a': 'handleActionManualClick',
            'tap .fn-action-reset a': 'handleActionResetClick'
        },

        initialize: function(options) {
            const { reverseProxyURL } = config.get('postcodeConfiguration');
            const isSwissPostcodeEnabled = config.get('isPostcodeEnabled') && config.get('postcodeProvider') === 'Swiss';

            this.options.isLookupEnabled = reverseProxyURL && isSwissPostcodeEnabled ;
            this.prepareOptions(options);
            this.initLookupControls();
        },

        prepareOptions: function(options) {
            const newOptions = { ...this.options, ...options };
            var innerFields = newOptions.innerFields;
            var sortByOrder = function(fields) {
                return _.chain(fields).sortBy('order').value();
            };
            var filterActive = function(fields) {
                return _.filter(fields, function(field) {
                    return field.active;
                });
            };
            var toArray = function(fields) {
                return _.chain(fields)
                    .pairs()
                    .map(function(pair) {
                        return pair[1];
                    })
                    .value();
            };
            var addDisabled = function(fields) {
                return _.map(fields, function(field) {
                    return _.extend({ disabled: true }, field);
                });
            };
            var addAsteriskFlag = (fields) =>
                fields.map(field => {
                    field.isLabelAsteriskEnabled = field.validationRules?.some((rule) => rule.type === 'required' && rule.isLabelAsteriskEnabled);
                    field.isPlaceholderAsteriskEnabled = field.validationRules?.some((rule) => rule.type === 'required' && rule.isPlaceholderAsteriskEnabled);

                    return field;
                });

            newOptions.innerFields = _.compose(
                addAsteriskFlag,
                sortByOrder,
                addDisabled,
                filterActive,
                toArray
            )(innerFields);

            this.options = newOptions;

        },

        initLookupControls: function() {
            const houseNumberInnerField = this.options.innerFields.find(field => field.type === 'houseNumber');
            const streetNameInnerField = this.options.innerFields.find(field => field.type === 'streetName');

            const houseNumberLookup = this.createLookupControl(houseNumberView, {
                id: this.options.id,
                hidden: this.options.isLookupEnabled,
                innerFields: ['houseNumber'],
                infoTooltip: houseNumberInnerField?.infoTooltip
            });

            const streetNameLookup = this.createLookupControl(streetNameView, {
                id: this.options.id,
                hidden: this.options.isLookupEnabled,
                innerFields: ['streetName'],
                observers: [houseNumberLookup],
                infoTooltip: streetNameInnerField?.infoTooltip
            });

            const zipCityLookup = this.createLookupControl(zipCityView, {
                id: this.options.id,
                label: this.options.label,
                placeholder: this.options.placeholder,
                infoTooltip: this.options.infoTooltip,
                isLabelAsteriskEnabled: this.options.isLabelAsteriskEnabled,
                isPlaceholderAsteriskEnabled: this.options.isPlaceholderAsteriskEnabled,
                innerFields: ['zip', 'city'],
                observers: [streetNameLookup, houseNumberLookup]
            });

            this.childViews = [
                zipCityLookup,
                streetNameLookup,
                houseNumberLookup
            ].filter(Boolean);
        },

        createLookupControl: function(View, fieldConfig) {
            const { name, form, innerFields: enabledInnerField } = this.options;
            const innerFields = enabledInnerField.filter((field) => fieldConfig.innerFields.includes(field.name));

            if (!innerFields.length) {
                return null;
            }

            if (innerFields.length === 1) {
                fieldConfig.label = fieldConfig.label || innerFields[0].label;
                fieldConfig.placeholder = fieldConfig.placeholder || innerFields[0].placeholder;
                fieldConfig.isLabelAsteriskEnabled = fieldConfig.isLabelAsteriskEnabled || innerFields[0].isLabelAsteriskEnabled;
                fieldConfig.isPlaceholderAsteriskEnabled = fieldConfig.isPlaceholderAsteriskEnabled || innerFields[0].isPlaceholderAsteriskEnabled;
            }

            return new View({
                ...fieldConfig,
                name,
                form,
                innerFields,
                autocompleteClassName: 'address-lookup__autocomplete'
            });
        },

        appendLookupControls: function() {
            const fragment = document.createDocumentFragment();

            this.childViews.forEach((view) => fragment.appendChild(view.render().el));
            this.$el.find('.fn-fields-container').append(fragment);
        },

        handleActionManualClick: function(event) {
            event.preventDefault();

            this.childViews.forEach((view) => view.enableManualInput());

            this.$el.find('.fn-action-manual').addClass('address-lookup-hidden');
            this.$el.find('.fn-action-reset').removeClass('address-lookup-hidden');
        },

        handleActionResetClick: function(event) {
            event.preventDefault();

            this.childViews.forEach((view) => view.reset());

            this.$el.find('.fn-action-manual').removeClass('address-lookup-hidden');
            this.$el.find('.fn-action-reset').addClass('address-lookup-hidden');
        },

        render: function() {
            this.$el.html(this.tmpl(this.options.template, this.options));
            this.$el.removeClass('fn-validate');

            this.appendLookupControls();

            return this;
        }

    });

});
